/**
 * Clarification MCP Tools
 *
 * Provides human-in-the-loop clarification for otters that encounter
 * ambiguity during execution.
 *
 * Two tools:
 * 1. stackwright_pro_clarify — mid-execution clarification (NOT upfront
 *    question collection; that's the Question Manifest Protocol).
 * 2. stackwright_pro_detect_conflict — pure-logic conflict detection between
 *    stated preferences and actual selections.
 *
 * Handler functions are exported for direct testing — no MCP server needed.
 */

import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import { jsonCoerce } from '../coerce.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ClarificationRequest {
  context?: string | undefined;
  question_type: 'closed_choice' | 'open_text';
  question: string;
  choices?: string[] | undefined;
  priority: 'blocking' | 'preferred' | 'optional';
  target_field?: string | undefined;
}

export interface ClarificationResponse {
  action: 'ask_user';
  adaptedQuestion?: AdaptedQuestion | undefined;
  prompt?: string | undefined;
  suggestedDefault?: string | undefined;
  targetField?: string | undefined;
}

/** Ready-to-pass shape for `ask_user_question`. */
interface AdaptedQuestion {
  question: string;
  header: string;
  options: Array<{ label: string; description?: string }>;
}

export interface ConflictDetectionInput {
  stated_preference: string;
  selected_values: Record<string, string>;
}

export interface ConflictDetectionResult {
  conflict: boolean;
  description?: string;
  resolution_options?: string[];
}

// ---------------------------------------------------------------------------
// Contradiction patterns — keyword matching against known opposites
// ---------------------------------------------------------------------------

const CONTRADICTION_PATTERNS: Array<{
  keywords: string[];
  conflicts: string[];
}> = [
  {
    keywords: ['minimal', 'clean', 'simple'],
    conflicts: ['vibrant', 'rich', 'content-heavy', 'playful'],
  },
  {
    keywords: ['dark', 'dark mode'],
    conflicts: ['light mode only', 'light'],
  },
  {
    keywords: ['enterprise', 'professional', 'corporate'],
    conflicts: ['playful', 'casual', 'fun'],
  },
  {
    keywords: ['accessible', 'wcag', 'section 508'],
    conflicts: ['compact', 'dense', 'small text'],
  },
  {
    keywords: ['government', 'defense', 'federal'],
    conflicts: ['public', 'no auth', 'consumer'],
  },
];

// ---------------------------------------------------------------------------
// Handler: clarify
// ---------------------------------------------------------------------------

export function handleClarify(input: ClarificationRequest): ClarificationResponse {
  const { question_type, question, choices, priority, target_field, context } = input;

  const base: ClarificationResponse = {
    action: 'ask_user',
    targetField: target_field,
  };

  if (question_type === 'closed_choice' && choices && choices.length > 0) {
    const header = truncateHeader(question);
    base.adaptedQuestion = {
      question,
      header,
      options: choices.map((c) => ({ label: c })),
    };
  } else {
    // open_text — foreman should ask the user directly
    const contextPrefix = context ? `Context: ${context}\n\n` : '';
    base.prompt = `${contextPrefix}${question}`;
  }

  if (priority === 'optional') {
    base.suggestedDefault = inferDefault(question_type, choices);
  }

  return base;
}

// ---------------------------------------------------------------------------
// Handler: detect conflict
// ---------------------------------------------------------------------------

export function handleDetectConflict(input: ConflictDetectionInput): ConflictDetectionResult {
  const preferenceLower = input.stated_preference.toLowerCase();
  const valuesLower = Object.values(input.selected_values).map((v) => v.toLowerCase());

  for (const pattern of CONTRADICTION_PATTERNS) {
    const preferenceMatch = pattern.keywords.some((kw) => preferenceLower.includes(kw));
    if (!preferenceMatch) continue;

    const conflictingValues = valuesLower.filter((val) =>
      pattern.conflicts.some((c) => val.includes(c))
    );

    if (conflictingValues.length > 0) {
      const matchedKeywords = pattern.keywords.filter((kw) => preferenceLower.includes(kw));
      const primaryKeyword = matchedKeywords[0] ?? 'preference';
      return {
        conflict: true,
        description:
          `Stated preference includes "${matchedKeywords.join(', ')}" ` +
          `but selections contain "${conflictingValues.join(', ')}" — these are contradictory.`,
        resolution_options: [
          `Align selections with the "${primaryKeyword}" preference`,
          `Revise stated preference to match current selections`,
          'Ask user which direction they actually want',
        ],
      };
    }
  }

  return { conflict: false };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Truncate to 50 chars (ask_user_question header limit). */
function truncateHeader(text: string): string {
  const MAX = 50;
  if (text.length <= MAX) return text;
  return text.slice(0, MAX - 1) + '…';
}

/** Best-effort default suggestion for optional questions. */
function inferDefault(
  questionType: ClarificationRequest['question_type'],
  choices?: string[]
): string {
  if (questionType === 'closed_choice' && choices && choices.length > 0) {
    return choices[0] ?? '(first choice)';
  }
  return '(use sensible project default)';
}

// ---------------------------------------------------------------------------
// MCP tool registration
// ---------------------------------------------------------------------------

export function registerClarificationTools(server: McpServer): void {
  // -----------------------------------------------------------------------
  // 1. stackwright_pro_clarify
  // -----------------------------------------------------------------------
  server.tool(
    'stackwright_pro_clarify',
    'Ask the user for clarification when a specialist otter encounters ambiguity. ' +
      'This is for MID-EXECUTION questions, NOT upfront question collection (use the Question Manifest Protocol for that). ' +
      'Returns a structured response for the foreman to present to the user via ask_user_question (closed_choice) or directly (open_text).',
    {
      context: z.string().optional().describe('Context about what the otter is trying to do'),
      question_type: z
        .enum(['closed_choice', 'open_text'])
        .describe('Type of question being asked'),
      question: z.string().describe('The clarification question to ask the user'),
      choices: jsonCoerce(z.array(z.string()).optional()).describe(
        'Options for closed_choice questions'
      ),
      priority: z
        .enum(['blocking', 'preferred', 'optional'])
        .optional()
        .default('preferred')
        .describe('How critical is this clarification? Default: preferred'),
      target_field: z.string().optional().describe('What field/config does this clarify?'),
    },
    async ({ context, question_type, question, choices, priority, target_field }) => {
      const result = handleClarify({
        context: context ?? undefined,
        question_type,
        question,
        choices: choices ?? undefined,
        priority: (priority ?? 'preferred') as ClarificationRequest['priority'],
        target_field: target_field ?? undefined,
      });

      return {
        content: [
          {
            type: 'text' as const,
            text:
              `🦦 Clarification needed${target_field ? ` for "${target_field}"` : ''}. ` +
              `Present the following to the user. ` +
              (result.adaptedQuestion
                ? 'Pass the adaptedQuestion to ask_user_question directly.'
                : 'Ask the user the prompt below.'),
          },
          {
            type: 'text' as const,
            text: JSON.stringify(result),
          },
        ],
      };
    }
  );

  // -----------------------------------------------------------------------
  // 2. stackwright_pro_detect_conflict
  // -----------------------------------------------------------------------
  server.tool(
    'stackwright_pro_detect_conflict',
    "Detect when a user's stated preference conflicts with their selected choices. " +
      'Uses keyword heuristics against known contradiction patterns (minimal vs vibrant, dark vs light, etc). ' +
      'Returns conflict details and resolution options.',
    {
      stated_preference: z.string().describe('What the user said they wanted'),
      selected_values: jsonCoerce(z.record(z.string(), z.string())).describe(
        'What the user actually selected (field → value)'
      ),
    },
    async ({ stated_preference, selected_values }) => {
      const result = handleDetectConflict({ stated_preference, selected_values });

      if (result.conflict) {
        return {
          content: [
            {
              type: 'text' as const,
              text:
                `⚠️ CONFLICT DETECTED\n\n` +
                `User stated: "${stated_preference}"\n` +
                `But selected: ${JSON.stringify(selected_values)}\n\n` +
                `${result.description}\n\n` +
                `Resolution options:\n${result.resolution_options?.map((o) => `  • ${o}`).join('\n')}\n\n` +
                `💡 Consider asking the user to reconcile this conflict.`,
            },
            {
              type: 'text' as const,
              text: JSON.stringify(result),
            },
          ],
        };
      }

      return {
        content: [
          {
            type: 'text' as const,
            text: '✅ No conflict detected between stated preference and selections.',
          },
          {
            type: 'text' as const,
            text: JSON.stringify(result),
          },
        ],
      };
    }
  );
}
